import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getRefSignupValue, clearUTMParams } from "@/utils/utmTracker";

const AuthForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "login";
  const navigate = useNavigate();
  const { toast } = useToast();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);

  // Check if the user is already logged in
  useEffect(() => {
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        navigate("/dashboard");
      }
    };

    checkSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session && !signupSuccess && mode === "login") {
        navigate("/dashboard");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, signupSuccess, mode]);


  useEffect(() => {
    if (mode === "login") {
      setSignupSuccess(false);
      setRedirectCountdown(5);
    }
  }, [mode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Simple validation
    if (!email || !password) {
      setError("Please fill in all required fields");
      return;
    }

    if (mode === "signup" && password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsLoading(true);

    try {
      if (mode === "signup") {
        const { data, error: signupError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${globalThis.location.origin}/auth?mode=login`,
          },
        });

        if (signupError) throw signupError;

        // Save UTM source to profile if available
        if (data?.user) {
          const refSignup = getRefSignupValue();
          if (refSignup) {
            const { error: rpcError } = await supabase.rpc('update_ref_signup', {
              user_id: data.user.id,
              ref_value: refSignup
            });
            
            if (rpcError) {
              console.error('Failed to save ref_signup:', rpcError);
            } 
          }
          clearUTMParams();
        } else {
          console.log('No user data available after signup');
        }
        setSignupSuccess(true);
      } else {
        const { data, error: signinError } =
          await supabase.auth.signInWithPassword({
            email,
            password,
          });

        if (signinError) throw signinError;

        if (data) {
          toast({
            title: "Logged in successfully!",
            description: "Welcome back to KeepMore.",
          });
          navigate("/dashboard");
        }
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "An error occurred during authentication";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (value: string) => {
    navigate(`/auth?mode=${value.toLowerCase()}`);
    setError(null);
    setSignupSuccess(false);
    setRedirectCountdown(5);
  };

  return (
    <div className="max-w-md w-full mx-auto">
      <Tabs value={mode} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid grid-cols-2 mb-6">
          <TabsTrigger value="login">Login</TabsTrigger>
          <TabsTrigger value="signup">Sign Up</TabsTrigger>
        </TabsList>

        <TabsContent value="login">
          <Card>
            <CardHeader>
              <CardTitle>Welcome back</CardTitle>
              <CardDescription>
                Enter your credentials to access your account
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <div className="flex justify-start">
                    <Button
                      type="button"
                      variant="link"
                      className="text-sm text-keepmore-purple p-0 h-auto"
                      onClick={async (e) => {
                        e.preventDefault();
                        if (!email) {
                          setError("Please enter your email address");
                          return;
                        }
                        try {
                          setIsLoading(true);
                          const { error: resetError } =
                            await supabase.auth.resetPasswordForEmail(email, {
                              redirectTo: `${globalThis.location.origin}/auth?mode=login`,
                            });
                          if (resetError) throw resetError;
                          toast({
                            title: "Password reset email sent",
                            description:
                              "If the email is associated with an account, an email will be sent to your inbox to reset your password.",
                          });
                        } catch (err: unknown) {
                          const errorMessage =
                            err instanceof Error
                              ? err.message
                              : "Failed to send reset email";
                          setError(errorMessage);
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                    >
                      Forgot password?
                    </Button>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button
                  type="submit"
                  className="w-full gradient-bg"
                  disabled={isLoading}
                >
                  {isLoading ? "Logging in..." : "Login"}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </TabsContent>

        <TabsContent value="signup">
          <Card>
            {!signupSuccess && (
              <CardHeader>
                <CardTitle>Create an account</CardTitle>
                <CardDescription>
                  Enter your details to get started with KeepMore
                </CardDescription>
              </CardHeader>
            )}
            {signupSuccess ? (
              <CardContent className="space-y-4 text-center py-8">
                <div className="space-y-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                    <svg
                      className="w-8 h-8 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-blue-600">
                      Account created successfully!
                    </h3>
                    <p className="text-sm text-gray-600 mt-2">
                      We've sent a verification email. Please check your inbox
                      and click the link to verify your account.
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Once verified, you can log in to your account.
                    </p>
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => {
                        setSignupSuccess(false);
                        navigate("/auth?mode=login");
                      }}
                    >
                      Go to Login
                    </Button>
                  </div>
                </div>
              </CardContent>
            ) : (
              <form onSubmit={handleSubmit}>
                <CardContent className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    type="submit"
                    className="w-full gradient-bg"
                    disabled={isLoading}
                  >
                    {isLoading ? "Creating account..." : "Sign Up"}
                  </Button>
                </CardFooter>
              </form>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AuthForm;